import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { CheckCircle2, XCircle, AlertTriangle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { dorsalesCarrera } from "@/lib/carrera.functions";
import { codigoCompra, modalidadLabels, participanteSchema, euro } from "@/lib/carrera.shared";

export const Route = createFileRoute("/dorsales")({
  validateSearch: (s) => z.object({ t: z.string().optional() }).parse(s),
  head: () => ({
    meta: [
      { title: "Entrega de dorsales · San Silvestre Sierra Norte" },
      { name: "description", content: "Página para el equipo de entrega de dorsales." },
      { property: "og:title", content: "Entrega de dorsales" },
      { property: "og:description", content: "Uso interno de la organización." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DorsalesPage,
});

const CLAVE_KEY = "dorsales-clave";
type Ficha = NonNullable<Awaited<ReturnType<typeof dorsalesCarrera>>>;

function tokenDe(texto: string) {
  try {
    const t = new URL(texto).searchParams.get("t");
    if (t) return t;
  } catch {
    /* not a URL */
  }
  return /^[0-9a-f-]{36}$/i.test(texto.trim()) ? texto.trim() : null;
}

function DorsalesPage() {
  const { t } = Route.useSearch();
  const navigate = useNavigate();
  const llamar = useServerFn(dorsalesCarrera);
  const [clave, setClave] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    setClave(sessionStorage.getItem(CLAVE_KEY));
    setListo(true);
  }, []);

  if (!listo) return <div className="fixed inset-0 bg-background" />;
  if (!clave)
    return (
      <PantallaClave
        onOk={(c) => {
          sessionStorage.setItem(CLAVE_KEY, c);
          setClave(c);
        }}
        comprobar={(c) => llamar({ data: { clave: c, token: "", marcar: false } })}
      />
    );
  const salir = () => {
    sessionStorage.removeItem(CLAVE_KEY);
    setClave(null);
  };
  return t ? (
    <PantallaFicha
      key={t}
      token={t}
      clave={clave}
      llamar={llamar}
      siguiente={() => navigate({ to: "/dorsales", search: {} })}
      claveMala={salir}
    />
  ) : (
    <Escaner onToken={(tok) => navigate({ to: "/dorsales", search: { t: tok } })} salir={salir} />
  );
}

function PantallaClave({ onOk, comprobar }: { onOk: (c: string) => void; comprobar: (c: string) => Promise<unknown> }) {
  const [valor, setValor] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function entrar(c = valor) {
    if (c.length !== 8) return setError("Escribe los 8 números");
    setBusy(true);
    setError("");
    try {
      await comprobar(c);
      onOk(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Clave incorrecta");
      setValor("");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-primary text-primary-foreground">
      <div className="h-3 bg-[repeating-linear-gradient(135deg,var(--navidad)_0_14px,var(--navidad-foreground)_14px_28px)]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 select-none text-primary-foreground/25">
        {[
          "left-[8%] top-[10%] text-3xl",
          "right-[12%] top-[16%] text-xl",
          "left-[20%] top-[34%] text-lg",
          "right-[6%] top-[42%] text-4xl",
          "left-[6%] bottom-[22%] text-4xl",
          "right-[18%] bottom-[14%] text-2xl",
          "left-[45%] top-[6%] text-xl",
          "left-[40%] bottom-[6%] text-lg",
        ].map((c, i) => (
          <span key={i} className={`absolute ${c}`}>
            ❄
          </span>
        ))}
      </div>
      <div className="relative flex flex-1 flex-col items-center justify-center gap-6 px-6 text-center">
        <div>
          <p className="text-xs tracking-[0.3em] text-primary-foreground/80">★ I EDICIÓN · 27·12·2026 ★</p>
          <h1 className="mt-3 font-serif text-4xl leading-tight">
            San Silvestre
            <span className="block text-2xl text-primary-foreground/90">de la Sierra Norte</span>
          </h1>
          <p className="mx-auto mt-3 w-fit rounded-full bg-navidad px-4 py-1 text-sm font-semibold text-navidad-foreground">
            <Lock className="mr-1 inline h-4 w-4" /> Entrega de dorsales
          </p>
        </div>
        <div className="w-full max-w-xs rounded-2xl bg-background p-5 text-foreground shadow-2xl">
          <p className="mb-3 text-sm text-muted-foreground">Clave de la organización</p>
          <div className="flex justify-center">
            <InputOTP
              maxLength={8}
              value={valor}
              onChange={(v) => {
                setValor(v.replace(/\D/g, ""));
                if (v.length === 8) entrar(v);
              }}
              inputMode="numeric"
              pattern="^[0-9]*$"
              autoFocus
            >
              <InputOTPGroup>
                {Array.from({ length: 8 }, (_, i) => (
                  <InputOTPSlot key={i} index={i} className="h-12 w-8 text-xl" />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          {error && <p className="mt-3 font-medium text-destructive">{error}</p>}
          <Button
            className="mt-4 h-14 w-full bg-navidad text-lg text-navidad-foreground hover:bg-navidad/90"
            onClick={() => entrar()}
            disabled={busy}
          >
            {busy ? "Comprobando…" : "Entrar"}
          </Button>
        </div>
        <p className="text-sm text-primary-foreground/80">La Nava de Jadraque · Arroyo de las Fraguas · El Ordial</p>
      </div>
      <div className="h-3 bg-[repeating-linear-gradient(135deg,var(--navidad)_0_14px,var(--navidad-foreground)_14px_28px)]" />
    </div>
  );
}

function Escaner({ onToken, salir }: { onToken: (t: string) => void; salir: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let scanner: { stop: () => void; destroy: () => void } | null = null;
    let hecho = false;
    let cancelado = false;
    (async () => {
      const { default: QrScanner } = await import("qr-scanner");
      if (cancelado || !video.current) return;
      const s = new QrScanner(
        video.current,
        (r) => {
          if (hecho) return;
          const tok = tokenDe(r.data);
          if (!tok) return setError("Este QR no es de una inscripción de la San Silvestre.");
          hecho = true;
          navigator.vibrate?.(150);
          s.stop();
          onToken(tok);
        },
        { preferredCamera: "environment", maxScansPerSecond: 5 },
      );
      scanner = s;
      try {
        await s.start();
      } catch {
        if (!cancelado) setError("No se pudo abrir la cámara. Permite el acceso a la cámara y recarga la página.");
      }
    })();
    return () => {
      cancelado = true;
      scanner?.stop();
      scanner?.destroy();
    };
  }, [onToken]);

  return (
    <div className="fixed inset-0 flex flex-col bg-foreground text-background">
      <video ref={video} className="absolute inset-0 h-full w-full object-cover" muted playsInline />
      <div className="relative flex items-center justify-between bg-foreground/70 px-4 pb-3 pt-[max(env(safe-area-inset-top),12px)]">
        <span className="text-lg font-semibold">Entrega de dorsales</span>
        <button onClick={salir} className="text-sm underline">
          Salir
        </button>
      </div>
      <div className="pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[70vw] max-w-xs -translate-x-1/2 -translate-y-1/2 rounded-3xl border-4 border-background/90" />
      <div className="relative mt-auto bg-foreground/70 px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-4 text-center">
        {error ? (
          <p className="rounded-lg bg-destructive p-3 font-medium text-destructive-foreground">{error}</p>
        ) : (
          <p>Apunta al QR del justificante</p>
        )}
      </div>
    </div>
  );
}

function PantallaFicha({
  token,
  clave,
  llamar,
  siguiente,
  claveMala,
}: {
  token: string;
  clave: string;
  llamar: (o: { data: { clave: string; token: string; marcar: boolean } }) => Promise<Ficha | null>;
  siguiente: () => void;
  claveMala: () => void;
}) {
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function pedir(marcar: boolean) {
    setBusy(true);
    try {
      const f = await llamar({ data: { clave, token, marcar } });
      setFicha(f);
    } catch (e) {
      const m = e instanceof Error ? e.message : "No se pudo leer el código";
      if (m === "Clave incorrecta") return claveMala();
      setError(m);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    pedir(false);
  }, []);

  const lista = ficha ? (z.array(participanteSchema).safeParse(ficha.participantes).data ?? []) : [];
  const conDorsal = lista.filter((p) => p.modalidad !== "acompanante");
  const acompanantes = lista.filter((p) => p.modalidad === "acompanante");
  const pagada = ficha?.estado === "pagada";
  const estado = !ficha
    ? null
    : !pagada
      ? { cls: "bg-destructive text-destructive-foreground", Icon: XCircle, txt: "NO VÁLIDA", sub: "No está pagada. No entregues nada." }
      : ficha.recienMarcada
        ? { cls: "bg-primary text-primary-foreground", Icon: CheckCircle2, txt: "ENTREGADO", sub: "Marcado como entregado." }
        : ficha.validado_at
          ? {
              cls: "bg-accent text-accent-foreground",
              Icon: AlertTriangle,
              txt: "YA ENTREGADA",
              sub: `Se entregó el ${new Date(ficha.validado_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}. No entregues otra vez.`,
            }
          : { cls: "bg-primary text-primary-foreground", Icon: CheckCircle2, txt: "VÁLIDA", sub: "Pagada y sin entregar." };

  return (
    <div className="mx-auto min-h-dvh max-w-md space-y-4 bg-background px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-[max(env(safe-area-inset-top),16px)]">
      <h1 className="font-serif text-xl text-primary">Entrega de dorsales</h1>
      {!ficha && !error && <p className="text-muted-foreground">Leyendo inscripción…</p>}
      {error && (
        <div className="rounded-xl bg-destructive p-5 text-center text-destructive-foreground">
          <XCircle className="mx-auto h-12 w-12" />
          <p className="mt-2 text-2xl font-bold">NO VÁLIDA</p>
          <p className="mt-1">{error}</p>
        </div>
      )}
      {ficha && estado && (
        <>
          <div className={`rounded-xl p-5 text-center ${estado.cls}`}>
            <estado.Icon className="mx-auto h-12 w-12" />
            <p className="mt-2 text-3xl font-bold">{estado.txt}</p>
            <p className="mt-1">{estado.sub}</p>
          </div>
          {pagada && (
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="rounded-xl border-2 border-primary p-4">
                <p className="text-5xl font-bold text-primary">{conDorsal.length}</p>
                <p className="text-sm">{conDorsal.length === 1 ? "dorsal" : "dorsales"}</p>
              </div>
              <div className="rounded-xl border-2 p-4">
                <p className="text-5xl font-bold">{lista.length}</p>
                <p className="text-sm">{lista.length === 1 ? "comida" : "comidas"}</p>
              </div>
            </div>
          )}
          <div className="rounded-xl border p-4">
            {conDorsal.length > 0 && <p className="mb-1 text-xs uppercase text-muted-foreground">Con dorsal</p>}
            <ul className="divide-y">
              {conDorsal.map((p, i) => (
                <li key={i} className="py-2">
                  <p className="text-lg font-semibold">{p.nombre}</p>
                  <p className="text-sm text-muted-foreground">
                    {modalidadLabels[p.modalidad]}
                    {p.grupo ? ` · ${p.grupo}` : ""}
                  </p>
                  {p.menor && (
                    <p className="mt-1 rounded bg-secondary px-2 py-1 text-sm font-medium">Menor · adulto: {p.tutor}</p>
                  )}
                </li>
              ))}
            </ul>
            {acompanantes.length > 0 && (
              <>
                <p className="mb-1 mt-3 text-xs uppercase text-muted-foreground">Acompañantes (sin dorsal)</p>
                <ul className="divide-y">
                  {acompanantes.map((p, i) => (
                    <li key={i} className="py-2 font-medium">
                      {p.nombre}
                      {p.menor ? ` · menor (${p.tutor})` : ""}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="mt-3 border-t pt-3 text-sm text-muted-foreground">
              {codigoCompra(ficha.id)} · {ficha.contacto_nombre} · {ficha.telefono} · {euro(Number(ficha.total))}
            </p>
          </div>
          {pagada && !ficha.validado_at && (
            <Button className="h-16 w-full text-lg" onClick={() => pedir(true)} disabled={busy}>
              {busy ? "Guardando…" : "Marcar como entregado"}
            </Button>
          )}
        </>
      )}
      <Button
        variant={ficha?.validado_at || error ? "default" : "outline"}
        className="h-16 w-full text-lg"
        onClick={siguiente}
      >
        Escanear otro QR
      </Button>
    </div>
  );
}
